import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, Loader2, Info, AlertTriangle, CheckCircle2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, pesanGalat } from "../lib/api";
import { TANDA_BARU_LOGIN } from "../lib/halamanAwal";
import { toast } from "sonner";
import SplashScreen from "../components/hris/SplashScreen";
import ForgotPasswordModal from "../components/ForgotPasswordModal";

const DEFAULT_HERO = "https://images.unsplash.com/photo-1606836379799-f88b03bc7039?crop=entropy&cs=srgb&fm=jpg&q=85";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [branding, setBranding] = useState({
    app_name: "ZOA OS",
    header_logo_url: "",
    login_subtitle_under_logo: "Sistem Informasi Karyawan",
    login_card_title: "Selamat datang kembali",
    login_card_subtitle: "Masuk untuk mengelola data karyawan, payroll, cuti dan performa.",
    login_hero_title: "Kelola tim ZOA Group dengan lebih rapi dan cepat.",
    login_hero_subtitle: "Dashboard karyawan, payroll otomatis, dan pemantauan performa dalam satu tempat.",
    login_hero_image_url: DEFAULT_HERO,
    login_show_demo_accounts: false,
    login_demo_notice: "",
    login_footer_note: "",
    login_notice_banner: "",
    login_notice_type: "info",
    login_button_label: "Masuk",
  });
  const [showSplash, setShowSplash] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  // Timer splash disimpan di ref dan dibersihkan saat Login unmount. Karyawan murni
  // tidak punya rute /login di pohonnya: begitu `login()` mengisi user, komponen ini
  // langsung diganti <Navigate "/"> dan unmount. Timer yang dibiarkan tetap menembak
  // `nav` dan menambah entri riwayat kedua — Back pertama jadi "diam".
  const timerRef = useRef(null);
  useEffect(() => () => clearTimeout(timerRef.current), []);
  useEffect(() => { api.get("/config/branding").then(r => setBranding(b => ({ ...b, ...r.data }))).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Login berhasil");
      setShowSplash(true);
      // Tujuan SELALU "/": halaman awal per akun diputuskan HalamanDepan (App.js) lewat
      // lib/halamanAwal.js — satu sumber untuk login DAN buka aplikasi. `replace` supaya
      // Back tidak kembali ke formulir login; penanda `baruLogin` supaya "/" tahu ini
      // kedatangan, bukan klik "Kembali ke HRIS".
      timerRef.current = setTimeout(() => nav("/", { replace: true, state: TANDA_BARU_LOGIN }), 1600);
    } catch (err) {
      toast.error(pesanGalat(err, "Login gagal"));
      setLoading(false);
    }
  };

  return (
    <>
      {showSplash && <SplashScreen branding={branding} />}
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left form */}
      <div className="flex items-center justify-center px-6 py-10 bg-white">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-10">
            {branding.header_logo_url ? (
              <img src={branding.header_logo_url} alt="logo" className="w-11 h-11 rounded-xl object-cover" />
            ) : (
              <div className="w-11 h-11 rounded-xl bg-[#0F172A] text-white grid place-items-center">
                <Building2 className="w-6 h-6" />
              </div>
            )}
            <div>
              <h1 className="font-display font-bold text-lg text-slate-900" data-testid="login-app-name">{branding.app_name}</h1>
              <p className="text-xs text-slate-500" data-testid="login-subtitle-logo">{branding.login_subtitle_under_logo}</p>
            </div>
          </div>

          {/* Notice banner opsional (maintenance / pengumuman) */}
          {branding.login_notice_banner && (
            <div className={`mb-5 rounded-lg border p-3 text-xs flex items-start gap-2 ${
              branding.login_notice_type === "warning" ? "bg-amber-50 border-amber-200 text-amber-800"
              : branding.login_notice_type === "success" ? "bg-emerald-50 border-emerald-200 text-emerald-800"
              : "bg-sky-50 border-sky-200 text-sky-800"
            }`} data-testid="login-notice-banner">
              {branding.login_notice_type === "warning" ? <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              : branding.login_notice_type === "success" ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
              : <Info className="w-4 h-4 shrink-0 mt-0.5" />}
              <span className="whitespace-pre-line">{branding.login_notice_banner}</span>
            </div>
          )}

          <h2 className="font-display text-2xl md:text-3xl font-bold text-slate-900" data-testid="login-card-title">{branding.login_card_title}</h2>
          <p className="mt-2 text-sm text-slate-500" data-testid="login-card-subtitle">{branding.login_card_subtitle}</p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Email</label>
              <input
                type="email" required value={email} onChange={e => setEmail(e.target.value)}
                data-testid="login-email"
                className="w-full h-11 px-4 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600/50 focus:border-emerald-600 transition-colors"
                placeholder="you@zoagroup.com"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Password</label>
              <input
                type="password" required value={password} onChange={e => setPassword(e.target.value)}
                data-testid="login-password"
                className="w-full h-11 px-4 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600/50 focus:border-emerald-600 transition-colors"
                placeholder="••••••••"
              />
            </div>
            <button
              type="submit" disabled={loading}
              data-testid="login-submit"
              className="w-full h-11 rounded-lg bg-[#059669] hover:bg-[#047857] text-white font-medium text-sm transition-colors active:scale-[.99] disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {branding.login_button_label || "Masuk"}
            </button>
            <div className="text-center">
              <button type="button" onClick={() => setShowForgot(true)}
                data-testid="link-forgot-password"
                className="text-xs text-emerald-700 hover:text-emerald-800 hover:underline font-medium">
                Lupa Password?
              </button>
            </div>
            {branding.login_footer_note && (
              <p className="text-[11px] text-center text-slate-400 mt-2 whitespace-pre-line" data-testid="login-footer-note">
                {branding.login_footer_note}
              </p>
            )}
          </form>

          {branding.login_show_demo_accounts && (
            <div className="mt-8 rounded-lg bg-slate-50 border border-slate-200 p-4 text-xs text-slate-600" data-testid="login-demo-box">
              <p className="font-semibold text-slate-700 mb-1">Akun demo:</p>
                {/* JANGAN PERNAH menulis email+password di berkas ini.
                    Tiga baris kredensial yang dulu ada di sini ikut ter-bundel
                    ke SETIAP build produksi, dan bundle itu bisa diunduh siapa
                    pun dari alamat publik. Saklar `login_show_demo_accounts`
                    hanya menyembunyikannya dari LAYAR, bukan dari berkasnya —
                    dan salah satu password itu terbukti MASIH BERLAKU di
                    produksi pada 24 Agu 2026 (ditemukan dengan mengunduh
                    bundle live lalu mencocokkan sidik passwordnya di server,
                    tanpa login). Kalau butuh petunjuk akun untuk demo, isi
                    `login_demo_notice` lewat Pengaturan Branding — nilainya
                    tersimpan di basis data dan tidak pernah ikut ke bundle. */}
              {branding.login_demo_notice ? (
                <p className="whitespace-pre-line">{branding.login_demo_notice}</p>
              ) : (
                <p className="text-slate-500">
                  Hubungi admin untuk mendapatkan akun.
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Right image */}
      <div className="hidden lg:block relative">
        <img
          src={branding.login_hero_image_url || DEFAULT_HERO}
          alt="Hero"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0F172A]/80 via-[#0F172A]/60 to-emerald-900/60" />
        <div className="relative h-full flex flex-col justify-end p-12 text-white">
          <h3 className="font-display text-4xl font-bold leading-tight max-w-md whitespace-pre-line" data-testid="login-hero-title">
            {branding.login_hero_title}
          </h3>
          <p className="mt-4 text-white/70 max-w-md whitespace-pre-line" data-testid="login-hero-subtitle">
            {branding.login_hero_subtitle}
          </p>
        </div>
      </div>
    </div>
    {showForgot && <ForgotPasswordModal onClose={() => setShowForgot(false)} />}
    </>
  );
}
